import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useFinancingBadge } from '../../hooks/useFinancingBadge';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import LoginScreen from '../../pages/admin/inventory/components/LoginScreen';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
  { path: '/admin/inventory', label: 'Inventory', icon: 'ri-car-line' },
  { path: '/admin/financing', label: 'Financing Apps', icon: 'ri-bank-card-line', badge: true },
  { path: '/admin/reviews', label: 'Reviews', icon: 'ri-star-line' },
  { path: '/admin/reviews/analytics', label: 'Review Analytics', icon: 'ri-bar-chart-line' },
  { path: '/admin/referrals', label: 'Referral Tracking', icon: 'ri-gift-line' },
  { path: '/admin/window-sticker', label: 'Window Sticker', icon: 'ri-file-text-line' },
  { path: '/admin/sticker-analytics', label: 'Sticker Analytics', icon: 'ri-line-chart-line' },
  { path: '/admin/images', label: 'Image Manager', icon: 'ri-image-line' },
  { path: '/admin/favicon', label: 'Favicon Manager', icon: 'ri-apps-line' },
  { path: '/admin/content', label: 'Site Content', icon: 'ri-file-edit-line' },
  { path: '/admin/feeds', label: 'Feeds', icon: 'ri-rss-line' },
  { path: '/admin/page-preview', label: 'Page Preview', icon: 'ri-eye-line' },
  { path: '/admin/dms', label: 'DMS Integration', icon: 'ri-database-line' },
  { path: '/admin/page-health', label: 'Page Health', icon: 'ri-heart-pulse-line' },
  { path: '/admin/setup', label: 'Admin Setup', icon: 'ri-shield-user-line' },
  { path: '/admin/security-test', label: 'Security Test', icon: 'ri-bug-line' },
];

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const navigate = useNavigate();
  const financingBadgeCount = useFinancingBadge();
  const { isAuthenticated } = useAdminAuth();

  // Wait one tick for AdminAuthContext to hydrate from localStorage
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 80);
    return () => clearTimeout(timer);
  }, []);

  // Spinner while hydrating
  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500">Verifying access…</p>
        </div>
      </div>
    );
  }

  // Show login screen for all admin pages when not authenticated
  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-[#1A2332] flex flex-col transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:z-auto`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white font-bold text-lg hover:text-[#E8B84B] transition-colors cursor-pointer"
          >
            <i className="ri-shield-star-line text-[#E8B84B]"></i>
            Admin Panel
          </button>
          <button
            className="lg:hidden text-white/60 hover:text-white cursor-pointer"
            onClick={() => setSidebarOpen(false)}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {navItems.map((item) => {
            const badgeCount = item.badge ? financingBadgeCount : 0;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-[#E8B84B] text-[#1A2332]'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </span>
                    <span className="flex-1 whitespace-nowrap">{item.label}</span>
                    {badgeCount > 0 && (
                      <span
                        className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 ${
                          isActive
                            ? 'bg-[#1A2332] text-[#E8B84B]'
                            : 'bg-red-500 text-white'
                        }`}
                      >
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-white/10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/50 hover:text-white text-xs transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Site
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile) */}
        <header className="lg:hidden sticky top-0 z-10 bg-white border-b border-gray-200 flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="w-9 h-9 flex items-center justify-center text-gray-600 hover:text-gray-900 cursor-pointer"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
          <span className="font-semibold text-gray-800 text-sm">Admin Panel</span>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
